<template>
  <div>
    <v-progress-circular v-if="loadingAtualizacaoView" size="20" indeterminate color="rgba(0, 0, 0, 0.87)" />
    <div v-else>
      <v-row>
        <v-col cols="8">
          <v-btn
            small
            title="Atualizar o resultado"
            outlined
            class="mb-n10 mr-2"
            :loading="loadingAtualizacaoResultado"
            @click="atualizarResultado"
          >
            <v-icon small color="primary">mdi-refresh</v-icon>
          </v-btn>
          <v-btn
            v-if="resultado.length > 1"
            small
            title="Baixar o resultado em Excel"
            outlined
            class="mb-n10 mr-2"
            :loading="loadingBaixarResultado"
            @click="baixarResultado"
          >
            <v-icon small color="primary">mdi-download</v-icon>
          </v-btn>
        </v-col>
        <v-col cols="4">
          <v-text-field
            v-if="resultado.length > 1"
            v-model="search"
            append-icon="mdi-magnify"
            class="break-search mb-5 ml-auto"
            label="Procurar"
            hide-details
            single-line
            style="max-width: 350px"
          />
        </v-col>
      </v-row>
      <v-data-table
        :headers="headers"
        :items="resultado"
        :search.sync="search"
        :mobile-breakpoint="800"
        :hide-default-footer="!resultado.length"
        no-data-text="Nenhuma linha de dados retornada pela consulta"
      >
        <template v-slot:[`item.ultima_atualizacao_em`]="{ item }">
          {{ item.ultima_atualizacao_em | parseToDateTimeBR }}
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <v-btn
            v-if="podeExportarDetalhamento(item)"
            class="pa-3"
            min-width="0"
            x-small
            outlined
            title="Exportar detalhamento dos registros desta linha"
            :loading="downloadProgress[getDownloadLoadId(item)]"
            @click="baixarDetalhamento(item)"
          >
            <v-icon small color="primary">mdi-download</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </div>
  </div>
</template>

<script>
  import dayjs from 'dayjs';
  import DiagnosticosPreEtapa17Service from '@/services/DiagnosticosPreEtapa17Service';
  export default {
    name: 'DiagnosticosPreEtapa17DataTable',
    props: {
      bdgdVersao: {
        type: Object,
        required: true
      },
      viewDiagnostico: {
        type: Object,
        required: true
      }
    },
    data: () => ({
      loadingAtualizacaoView: false,
      loadingAtualizacaoResultado: false,
      loadingBaixarResultado: false,
      search: null,
      resultado: [],
      downloadProgress: {}
    }),
    computed: {
      headers() {
        if (!this.resultado.length) {
          return [];
        }
        const headers = Object.keys(this.resultado[0]).map((header) => ({
          text: header.toUpperCase(),
          value: header
        }));
        if (this.hasDetalhamentoPorLinha) {
          headers.push({
            text: 'AÇÕES',
            value: 'actions',
            align: 'center',
            sortable: false,
            width: 96
          });
        }
        return headers;
      },

      configuracaoDetalhamento() {
        const configuracoes = {
          pbi_atributos_gerais_resumo: {
            tipo: 'atributosGerais',
            requerId: true,
            nomeArquivo: 'atributos-gerais',
            identificador: 'id',
            servico: DiagnosticosPreEtapa17Service.baixarDetalhamentoAtributosGerais
          },

          pbi_trafo_diferente: {
            tipo: 'trafoDiferente',
            requerId: false,
            nomeArquivo: 'trafo-diferente',
            identificador: 'tabela',
            servico: DiagnosticosPreEtapa17Service.baixarDetalhamentoTrafoDiferente
          },

          pbi_conectividade: {
            tipo: 'conectividade',
            requerId: false,
            nomeArquivo: 'conectividade',
            identificador: 'tabela',
            servico: DiagnosticosPreEtapa17Service.baixarDetalhamentoConectividade
          }
        };

        return configuracoes[this.viewDiagnostico?.view] ?? null;
      },
      hasDetalhamentoPorLinha() {
        return this.configuracaoDetalhamento !== null;
      }
    },
    mounted() {
      this.getDadosViewDiagnostico();
    },
    methods: {
      getDadosViewDiagnostico() {
        this.loadingAtualizacaoView = true;
        DiagnosticosPreEtapa17Service.getDadosViewDiagnostico(this.bdgdVersao.id, this.viewDiagnostico.view)
          .then(({ data }) => {
            this.resultado = data;
          })
          .catch((error) => {
            this.$toast.error(`Erro ao buscar os dados do diagnóstico para "${this.viewDiagnostico.nome}".`, '', {
              position: 'topRight'
            });
            console.error(error);
          })
          .finally(() => (this.loadingAtualizacaoView = false));
      },
      atualizarResultado() {
        this.loadingAtualizacaoResultado = true;
        DiagnosticosPreEtapa17Service.atualizaDadosViewDiagnostico(this.bdgdVersao.id, this.viewDiagnostico.view)
          .then(() => this.getDadosViewDiagnostico())
          .catch((error) => {
            this.$toast.error(`Erro ao atualizar os dados do diagnóstico para "${this.viewDiagnostico.nome}".`, '', {
              position: 'topRight'
            });
            console.error(error);
          })
          .finally(() => (this.loadingAtualizacaoResultado = false));
      },
      podeExportarDetalhamento(item) {
        const configuracao = this.configuracaoDetalhamento;

        if (configuracao === null || this.getRowTabela(item) === null) {
          return false;
        }

        return !configuracao.requerId || this.getRowId(item) !== null;
      },
      getRowValue(item, property) {
        return item?.[property] ?? null;
      },
      getRowId(item) {
        return this.getRowValue(item, 'id');
      },
      getRowTabela(item) {
        return this.getRowValue(item, 'tabela');
      },
      baixarDetalhamento(item) {
        const tabela = this.getRowTabela(item);

        if (tabela === null) {
          this.$toast.error('Não foi possível identificar a tabela da linha selecionada.', '', {
            position: 'topRight'
          });
          return;
        }

        const detalhamento = this.getDetalhamento(item);

        if (detalhamento === null) {
          return;
        }

        const { loadId } = detalhamento;
        this.$toast.info('Preparando seu arquivo para download.', '', {
          position: 'topRight',
          timeout: 2000
        });

        this.iniciarDownload(loadId);

        detalhamento
          .servico(...detalhamento.parametros, {
            timeout: 60 * 60 * 1000
          })
          .then((res) => {
            this.baixarArquivo(res.data, detalhamento.arquivo);
          })
          .catch((error) => {
            this.$toast.error('Falha no download do detalhamento da linha selecionada.', '', {
              position: 'topRight'
            });
            console.error(error);
          })
          .finally(() => {
            this.finalizarDownload(loadId);
          });
      },

      getDetalhamento(item) {
        const configuracao = this.configuracaoDetalhamento;

        if (configuracao === null) {
          return null;
        }

        const identificador = this.getRowValue(item, configuracao.identificador);

        if (identificador === null) {
          return null;
        }

        return {
          parametros: configuracao.requerId
            ? [this.bdgdVersao.id, identificador, this.getRowTabela(item)]
            : [this.bdgdVersao.id, this.getRowTabela(item)],
          arquivo: this.montarNomeArquivoDetalhamento(item),
          servico: configuracao.servico,
          loadId: this.getDownloadLoadId(item)
        };
      },

      montarNomeArquivoDetalhamento(item) {
        const configuracao = this.configuracaoDetalhamento;
        const identificador = this.getRowValue(item, configuracao.identificador);
        const timestamp = dayjs().format('YYYYMMDDHHmm');

        return `detalhamento-${configuracao.nomeArquivo}-${identificador}-${timestamp}.xlsx`;
      },

      getDownloadLoadId(item) {
        const configuracao = this.configuracaoDetalhamento;
        const valores = [this.bdgdVersao.id];

        if (configuracao?.identificador === 'id') {
          valores.push(this.getRowId(item));
        }

        valores.push(this.getRowTabela(item));

        return valores.join('-');
      },

      iniciarDownload(loadId) {
        this.$set(this.downloadProgress, loadId, true);
      },

      finalizarDownload(loadId) {
        this.$set(this.downloadProgress, loadId, false);
      },
      baixarArquivo(data, arquivo) {
        const url = window.URL.createObjectURL(new Blob([data]));
        const link = document.createElement('a');

        link.href = url;
        link.setAttribute('download', arquivo);

        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(url);
      },

      baixarResultado() {
        this.loadingBaixarResultado = true;
        DiagnosticosPreEtapa17Service.baixarResultadoDadosViewDiagnostico(this.bdgdVersao.id, this.viewDiagnostico.view, {
          timeout: 60 * 60 * 1000
        })
          .then((res) => {
            const timestamp = dayjs().format('YYYYMMDDHHmm');
            const nomeViewDiagnostico = this.viewDiagnostico.view.replaceAll('_', '-');
            const arquivo = `resultado-diagnostico-${nomeViewDiagnostico}-${timestamp}.xlsx`;

            this.baixarArquivo(res.data, arquivo);
          })
          .catch(() => {
            this.$toast.error('Falha no download do arquivo resultado.', '', {
              position: 'topRight'
            });
          })
          .finally(() => (this.loadingBaixarResultado = false));
      }
    }
  };
</script>
